import { BaseApi } from "@/api/base";
import type { QueryParams } from "@/api/types";
import type { Response } from "@/api/client";
import type { FileDto } from "@/api/files/dto/file.dto";
import type { ListResponseDto } from "@/api/dto/list-response.dto";
import type {
  CreateOrderExpense,
  CreateOrderPayment,
  OrderDto,
  OrderExpense,
  OrderListItemDto,
  OrderPayment,
  PatchOrderDto,
  UpdateOrderExpense,
  UpdateOrderPayment,
} from "@/api/orders/dto/order.dto";

export class OrdersApi extends BaseApi {
  private ordersListController: AbortController | undefined;

  findOrderFiles(orderId: string): Promise<Response<FileDto[]>> {
    return this.client.get<FileDto[]>("orders/" + orderId + "/files");
  }

  async findOrders(
    options: QueryParams = {},
    abortable: boolean = true,
  ): Promise<Response<ListResponseDto<OrderListItemDto>>> {
    if (abortable && this.ordersListController) {
      this.ordersListController.abort();
    }
    this.ordersListController = new AbortController();

    const response: Response<ListResponseDto<OrderListItemDto>> = await this.client.get("orders", options, {
      signal: abortable ? this.ordersListController.signal : undefined,
    });
    this.ordersListController = undefined;
    return response;
  }

  findOrderById(orderId: string): Promise<Response<OrderDto>> {
    return this.client.get("orders/" + orderId);
  }

  upsertOrder(order: any): Promise<Response<OrderDto>> {
    return this.client.save("orders", order);
  }

  findPolylineByOrderId(orderId: string): Promise<Response<string>> {
    return this.client.get("orders/" + orderId + "/polyline");
  }

  patchOrder(orderId: string, data: PatchOrderDto): Promise<Response<OrderDto>> {
    return this.client.patch("orders/" + orderId, data);
  }

  createOrderPayment(orderId: string, payment: CreateOrderPayment): Promise<Response<OrderPayment>> {
    return this.client.post("orders/" + orderId + "/payments", payment);
  }

  updateOrderPayment(
    orderId: string,
    paymentId: number,
    payment: UpdateOrderPayment,
  ): Promise<Response<OrderPayment>> {
    return this.client.patch("orders/" + orderId + "/payments/" + paymentId, payment);
  }

  createOrderExpense(orderId: string, expense: CreateOrderExpense): Promise<Response<OrderExpense>> {
    return this.client.post("orders/" + orderId + "/expenses", expense);
  }

  updateOrderExpense(
    orderId: string,
    expenseId: number,
    expense: UpdateOrderExpense,
  ): Promise<Response<OrderExpense>> {
    return this.client.patch("orders/" + orderId + "/expenses/" + expenseId, expense);
  }

  deleteOrderExpense(orderId: string, expenseId: number): Promise<Response<void>> {
    return this.client.delete("orders/" + orderId + "/expenses/" + expenseId);
  }

  deleteOrderPayment(orderId: string, paymentId: number): Promise<Response<void>> {
    return this.client.delete("orders/" + orderId + "/payments/" + paymentId);
  }
}
